import api from '@/api';
import dayjs from 'dayjs';
// vuex store
import store from '@/store';

/**
 * @class Action
 */
class Action {
  /**
   * Get a list of client actions
   *
   * @param params
   * @param cancelSource
   * @returns {AxiosPromise<any>}
   */
  static list(params, cancelSource = null) {
    if (cancelSource) {
      params.cancelToken = cancelSource.token;
    }
    return api.get('/actions/riskhub', params);
  }

  /**
   * Retrieve an action by its clientActionId
   *
   * @param id
   * @returns {*}
   */
  static find(id) {
    return api.get(`/actions/riskhub/${id}`);
  }

  /**
   * Gets counts for actions key'd by status
   *
   * @param complianceTypeId
   * @param propertyTenure
   * @returns {Promise<AxiosResponse<T>>}
   */
  static getCounts(complianceTypeId, propertyTenure) {
    complianceTypeId = complianceTypeId || store.state.complianceType.complianceTypeId;
    return api.get('/actions/riskhub/counts', { params: { complianceTypeId, propertyTenure } });
  }

  /**
   * Gets counts for each priority
   *
   * @param complianceTypeId
   * @param interval
   * @param propertyTenure
   * @returns {Promise<AxiosResponse<T>>}
   */
  static getPriorityCounts(complianceTypeId, interval, propertyTenure) {
    return api.get('/actions/riskhub/counts/priorities', { params: { complianceTypeId, interval, propertyTenure } });
  }

  /**
   * Gets extended counts
   *
   * @param complianceTypeId
   * @param propertyTenure
   * @returns {Promise<AxiosResponse<T>>}
   */
  static getExtendedCounts(complianceTypeId, propertyTenure) {
    return api.get('/actions/riskhub/counts/extended', { params: { complianceTypeId, propertyTenure } });
  }

  /**
   * Returns tags
   * @param {boolean} includeInactive - Whether to include inactive tags in the response
   * @returns {AxiosPromise<any>}
   */
  static getTags({ includeInactive = false } = {}) {
    const params = includeInactive ? { includeInactive: 1 } : {};
    return api.get('/actions/riskhub/tags', { params });
  }

  /**
   * Returns tags for selected actions
   *
   * @returns {Promise<AxiosResponse<T>>}
   */
  static getTagsForActions(actionIds = []) {
    // Using post due to the potential amount of actionIds
    return api.post(
      'actions/riskhub/tags',
      { actionIds },
    );
  }

  /**
   * Untag one or more actions
   *
   * @param clientActionIds
   * @param tags
   * @returns {JQuery.jqXHR | JQueryXHR | AxiosPromise<any>}
   */
  static untag(clientActionIds, tags) {
    return api.post(
      '/actions/riskhub/tags/bulk-untag',
      {
        clientActionIds,
        tags,
      },
    );
  }

  /**
   * Tag one or more actions with tags
   * @param clientActionIds
   * @param tags
   * @returns {JQuery.jqXHR | JQueryXHR | AxiosPromise<any>}
   */
  static tag(clientActionIds, tags) {
    let params = {
      clientActionIds,
      tags,
    };
    let endpoint = 'tags/bulk';

    if (!Array.isArray(clientActionIds)) {
      endpoint = `${clientActionIds}/tags`;
      params = {
        clientActionId: clientActionIds,
        tags,
      };
    }

    return api.post(`/actions/riskhub/${endpoint}`, params);
  }

  /**
   * Attach one or more actions with work plans
   * @param clientActionIds
   * @param workPlanId
   * @returns {JQuery.jqXHR | JQueryXHR | AxiosPromise<any>}
   */
  static workPlan(clientActionIds, workPlanId) {
    return api.post(`/actions/riskhub/work-plans/${workPlanId}/add-actions`, { clientActionIds });
  }

  /**
   * Remove one or more actions from work plans
   * @param clientActionIds
   * @returns {JQuery.jqXHR | JQueryXHR | AxiosPromise<any>}
   */
  static removeWorkPlan(clientActionIds) {
    return api.post(`/actions/riskhub/work-plans/bulk-remove`, { clientActionIds });
  }

  /**
   *
   * @param clientActionId
   * @returns {*}
   */
  static getTeamManagers(clientActionId) {
    return api.get(`/actions/riskhub/${clientActionId}/managers`);
  }

  /**
   * Responds to a query
   * @param clientActionId
   * @param note
   * @returns { AxiosPromise<any>}
   */
  static respondToQuery({ clientActionId, note }) {
    return api.post(`/actions/riskhub/${clientActionId}/status/query/respond`, { clientActionId, note });
  }

  /**
   * Escalates  a query
   *
   * @param clientActionId
   * @param note
   * @returns { AxiosPromise<any>}
   */
  static escalateQuery({
    clientActionId, query, teamId, userId,
  }) {
    return api.post(`/actions/riskhub/${clientActionId}/status/query/escalate`, {
      clientActionId, query, teamId, userId,
    });
  }

  /**
   * Submit a query
   *
   * Submit queries to multiple client actions
   * @returns {Promise<AxiosResponse<T>>}
   * @param clientActionIds
   * @param query
   */
  static bulkQuery(actionIds, query, teamId, userId) {
    return api.post('/actions/riskhub/query/bulk', {
      actionIds, query, teamId, userId,
    });
  }

  /**
   * Rejects an action
   * @param clientActionId
   * @param note
   * @returns { AxiosPromise<any>}
   */
  static submitQuery({
    clientActionId, query, teamId, userId,
  }) {
    return api.post(`/actions/riskhub/${clientActionId}/status/query`, {
      clientActionId, query, userId, teamId,
    });
  }

  /**
   * Rejects an action
   * @param clientActionId
   * @param note
   * @returns { AxiosPromise<any>}
   */
  static respondToNoAccess({ clientActionId, note }) {
    return api.post(`/actions/riskhub/${clientActionId}/status/no-access/respond`, { clientActionId, note });
  }

  /**
   * Rejects an action
   * @param clientActionId
   * @param note
   * @returns { AxiosPromise<any>}
   */
  static noAccess({ clientActionId, note }) {
    return api.post(`/actions/riskhub/${clientActionId}/status/no-access`, { clientActionId, note });
  }

  /**
   * Rejects an action
   * @param clientActionId
   * @param note
   * @returns { AxiosPromise<any>}
   */
  static reject({ clientActionId, note }) {
    return api.post(`/actions/riskhub/${clientActionId}/reject`, { clientActionId, note });
  }

  /**
   * Adds a note against an action
   * @param clientActionId
   * @param note
   * @returns { AxiosPromise<any>}
   */
  static addNote({ clientActionId, note }) {
    return api.post(`/actions/riskhub/${clientActionId}/note`, { note });
  }

  /**
   * Adds a note against multiple actions
   *
   * @param clientActionIds
   * @param note
   * @returns { AxiosPromise<any>}
   */
  static bulkAddNote(clientActionIds, note) {
    return api.post('/actions/riskhub/note', { clientActionIds, note });
  }

  /**
   * Approves an action
   *
   * @param clientActionId
   * @param note
   * @param files
   * @returns { AxiosPromise<any>}
   */
  static approve({ clientActionId, note = null, files = null }) {
    const params = {
      clientActionId,
    };

    if (note) {
      params.note = note;
    }

    if (files) {
      params.files = files;
    }

    return api.post(`/actions/riskhub/${clientActionId}/approve`, params);
  }

  /**
   * Unapproves an action
   *
   * @param clientActionId
   * @param reason
   * @returns { AxiosPromise<any>}
   */
  static unapprove({ clientActionId, reason }) {
    return api.post(`/actions/riskhub/${clientActionId}/unapprove`, { clientActionId, reason });
  }

  /**
   * Bulk approve multiple actions
   * @param clientActionIds
   * @param note
   * @param files
   * @returns { AxiosPromise<any>}
   */
  static bulkApprove({ clientActionIds, note = null, files = null }) {
    return api.post('/actions/riskhub/approve/bulk', { clientActionIds, note, files });
  }

  /**
   * Bulk unapprove multiple actions
   * @param clientActionIds
   * @param reason
   * @returns { AxiosPromise<any>}
   */
  static bulkUnapprove({ clientActionIds, reason }) {
    return api.post('/actions/riskhub/unapprove/bulk', { clientActionIds, reason });
  }

  /**
   * Partially complete an action
   *
   * @param clientActionId
   * @returns { AxiosPromise<any>}
   */
  static partiallyComplete(clientActionId, note) {
    return api.post(`/actions/riskhub/${clientActionId}/partially-complete`, { note });
  }

  /**
   * Update an actions internal due date
   * @param clientActionId
   * @param internalDueDate
   * @returns {AxiosPromise<any> | IDBRequest<IDBValidKey> | Promise<void>}
   */
  static updateInternalDueDate({ clientActionId, internalDueDate }) {
    return api.put(`/actions/riskhub/${clientActionId}`, { internalDueDate });
  }

  /**
   * Update an action's date referred to legal
   * @param clientActionId
   * @param dateReferredToLegal
   * @returns {AxiosPromise<any> | IDBRequest<IDBValidKey> | Promise<void>}
   */
  static updateDateReferredToLegal({ clientActionId, dateReferredToLegal }) {
    return api.put(`/actions/riskhub/${clientActionId}`, { dateReferredToLegal });
  }

  /**
   * Update an action's extended due date
   * @param clientActionId
   * @param extendedDueDate
   * @returns {AxiosPromise<any> | IDBRequest<IDBValidKey> | Promise<void>}
   */
  static updateExtendedDueDate({ clientActionId, extendedDueDate }) {
    return api.put(`/actions/riskhub/${clientActionId}`, { extendedDueDate });
  }

  /**
   * Update an action's work order
   * @param clientActionId
   * @param workOrder
   * @returns {AxiosPromise<any> | IDBRequest<IDBValidKey> | Promise<void>}
   */
  static updateWorkOrder({ clientActionId, workOrder }) {
    return api.put(`/actions/riskhub/${clientActionId}`, { workOrder });
  }

  /**
   * Bulk update internal due dates for multiple actions
   * @param clientActionIds
   * @param internalDueDate
   * @returns {AxiosPromise<any>}
   */
  static bulkUpdateInternalDueDate(clientActionIds, internalDueDate) {
    return api.post('/actions/riskhub/internal-due-dates/bulk', {
      action_ids: clientActionIds,
      internal_due_date: internalDueDate,
    });
  }

  /**
   * Bulk update extended due dates for multiple actions
   * @param clientActionIds
   * @param extendedDueDate
   * @returns {AxiosPromise<any>}
   */
  static bulkUpdateExtendedDueDate(clientActionIds, extendedDueDate) {
    return api.post('/actions/riskhub/extended-due-dates/bulk', {
      action_ids: clientActionIds,
      extended_due_date: extendedDueDate,
    });
  }

  /**
   * Downloads PDF of actions
   * @param clientActionIds
   * @param reviewer
   * @returns { AxiosPromise<any>}
   */
  static downloadPdf({ clientActionIds, reviewer }) {
    return api.post('/actions/riskhub/download', {
      clientActionIds, reviewer,
    }, {
      responseType: 'arraybuffer',
    }).then((response) => {
      const filename = `report_${dayjs().format('YYYY-MM-DD').toString()}.pdf`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
    });
  }

  /**
   * Retrieve photos relating to the action (recommendation)
   * @param clientActionId
   * @returns {*}
   */
  static getPhotos({ clientActionId }) {
    return api.get(`/actions/riskhub/${clientActionId}/photos`);
  }

  /**
   * Retrieve photos relating to the action's previous recommendation onsite photos
   * @param clientActionId
   * @returns {*}
   */
  static getPreviousRecommendationOnsitePhotos({ clientActionId }) {
    return api.get(`/actions/riskhub/${clientActionId}/previous-recommendation-photos`);
  }

  /**
   * Retrieve photos relating to the action (recommendation)
   * @param clientActionId
   * @returns {*}
   */
  static getNotes({ clientActionId }) {
    return api.get(`/v1/actions/riskhub/${clientActionId}/notes`);
  }

  /**
   * Retrieve linked Client action to give Riskhub action(recommendation)
   * @param riskHubActionId
   * @returns {*}
   */
  static getLinkedClientActions({ actionId }) {
    return api.get(`/actions/riskhub/${actionId}/linked`);
  }

  /**
   * Download all actions
   * @param complianceTypeId
   * @returns {*}
   */
  static downloadAll(complianceTypeId) {
    return api.get(`/actions/riskhub/download?complianceTypeId=${complianceTypeId}`);
  }
}

export default Action;
